import { useEffect, useState } from "react";
import api from "../services/api"; 
import { getProductos } from "../services/productoService"; 
// 🛑 Importar la nueva función de eliminación del servicio
import { deletePresupuesto } from "../services/presupuestoService"; 
import { formatNumber } from "../utils/numberFormat";
import jsPDF from "jspdf";
import "jspdf-autotable";

const cargarLogoShastino = async () => {
  const response = await fetch(`${process.env.PUBLIC_URL}/shastino-logo.png`);
  if (!response.ok) {
    throw new Error(`No se pudo cargar el logo de Shastino (${response.status})`);
  }

  const logoBlob = await response.blob();
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      if (typeof reader.result === "string") {
        resolve(reader.result);
      } else {
        reject(new Error("El logo de Shastino no se pudo convertir para el PDF"));
      }
    };
    reader.onerror = () => reject(reader.error || new Error("Error al leer el logo de Shastino"));
    reader.readAsDataURL(logoBlob);
  });
};

const dibujarEncabezadoPresupuesto = (doc, logo, fecha, cliente) => {
  const anchoPagina = doc.internal.pageSize.getWidth();
  doc.setFillColor(17, 42, 68);
  doc.rect(0, 0, anchoPagina, 38, "F");
  doc.setFillColor(231, 76, 60);
  doc.rect(0, 38, anchoPagina, 2, "F");
  doc.addImage(logo, "PNG", 15, 7, 58, 20);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(15);
  doc.setTextColor(255, 255, 255);
  doc.text("PRESUPUESTO", anchoPagina - 15, 17, { align: "right" });
  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);
  doc.text(`Fecha: ${fecha}`, anchoPagina - 15, 25, { align: "right" });
  doc.setTextColor(0, 0, 0);
  doc.setFont("helvetica", "bold");
  doc.text(`Cliente: ${cliente || "Sin especificar"}`, 15, 49);
  return 58;
};

const dibujarContactoPresupuesto = (doc, y, margenIzq, margenDer) => {
  const limitePagina = doc.internal.pageSize.getHeight();
  let inicio = Math.max(y + 8, limitePagina - 25);
  if (inicio + 16 > limitePagina - 5) {
    doc.addPage();
    inicio = 18;
  }

  doc.setDrawColor(205, 213, 222);
  doc.line(margenIzq, inicio, margenDer, inicio);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(8);
  doc.setTextColor(70, 80, 90);
  doc.text("Instagram: @shastino_equipamientos  |  WhatsApp: 11 6245-7151", margenIzq, inicio + 6);
  doc.text("Dardo Rocha 4362, El Jagüel  |  www.shastino.com.ar", margenIzq, inicio + 12);
  doc.setTextColor(0, 0, 0);
};

export default function PresupuestosPage() {
  const [presupuestos, setPresupuestos] = useState([]);
  const [descripcion, setDescripcion] = useState("");
  const [cliente, setCliente] = useState("");
  const [productos, setProductos] = useState([]);
  const [itemsSeleccionados, setItemsSeleccionados] = useState([]); // {nombre, cantidad, precio}
  const [tipoPlantilla, setTipoPlantilla] = useState("cercano");
  
  // 🆕 CONFIGURACIÓN TÉCNICA COMPLETA DEL PRESUPUESTO (TODO editable)
  const [configTecnica, setConfigTecnica] = useState({
    // MEDIDAS
    medidas: "", // Ej: "4.00 x 4.00 x 2.06"
    
    // PANELES
    espesorPaneles: "",
    aislamiento: "",
    revestimiento: "",
    unionPaneles: "",
    refuerzo: "",
    piso: "",
    
    // PUERTA
    tipoPuerta: "batiente",
    anchoPuerta: "",
    detallesPuerta: "",
    
    // EQUIPAMIENTO - CERCANO
    hpUnidadCercano: "",
    tipoMotorCercano: "trifásica",
    marcaUnidadCercano: "",
    hpEvaporadorCercano: "",
    tipoEvaporadorCercano: "",
    
    // EQUIPAMIENTO - INTERIOR
    hpUnidadInterior: "",
    tipoMotorInterior: "monofásica",
    marcaUnidadInterior: "",
    hpEvaporadorInterior: "",
    tipoEvaporadorInterior: "",
    
    // MATERIALES DE FRÍO
    metrosCano: "",
    detallesMaterialesFrio: "",
    
    // MATERIALES ELÉCTRICOS
    metrosCable: "",
    detallesMaterialesElectricos: "",
    
    // TABLERO ELÉCTRICO
    detallesTablero: ""
    
    // incluyeArmado e incluyeFlete se calculan automáticamente según plantilla
  });

  useEffect(() => {
    load();
    loadProductos();
  }, []);

  // CARGAR PRESUPUESTOS (CORREGIDO con API para JWT)
  const load = async () => {
    try {
      const res = await api.get("/presupuestos"); 
      setPresupuestos(res.data);
    } catch (error) {
      console.error("Error al cargar presupuestos (posible 403):", error);
      // Puedes agregar un alert aquí si el error 403 persiste
    }
  };

  const loadProductos = async () => {
    const data = await getProductos();
    setProductos(data);
  };

  const handleAddItem = (producto) => {
    // si ya existe, incremento cantidad
    const exists = itemsSeleccionados.find((i) => i.nombre === producto.nombre);
    if (exists) {
      setItemsSeleccionados((prev) => prev.map((i) => i.nombre === producto.nombre ? { ...i, cantidad: i.cantidad + 1 } : i));
    } else {
      // precio: tomamos el costo+ganancia si backend no lo proporciona; si producto tiene costoTotal y ganancia, usamos precio = costoTotal + ganancia
      const costoTotal = producto.costoTotal ?? producto.items.reduce((s, it) => s + Number(it.costo || 0), 0);
      const ganancia = producto.gananciaGenerada ?? costoTotal * ((Number(producto.porcentajeGanancia) || 0) / 100);
      const precio = Number((costoTotal + ganancia).toFixed(2));
      setItemsSeleccionados((prev) => [...prev, { nombre: producto.nombre, cantidad: 1, precio }]);
    }
  };

  const handleRemoveItem = (nombre) => {
    setItemsSeleccionados((prev) => prev.filter((i) => i.nombre !== nombre));
  };

  // CREAR PRESUPUESTO (CORREGIDO con API para JWT)
  const handleCreate = async () => {
    if (!descripcion) {
      alert("Agregá una descripción.");
      return;
    }
    // Sanitize descripción (eliminar caracteres invisibles que a veces vienen al pegar desde otras apps)
    const safeDescripcion = (descripcion || "").replace(/[\u200B-\u200D\uFEFF]/g, '').trim();
    const safeCliente = (cliente || "").replace(/[\u200B-\u200D\uFEFF]/g, '').trim();

    const payload = {
      descripcion: safeDescripcion,
      cliente: safeCliente,
      items: itemsSeleccionados,
      // guardamos el tipo de plantilla elegido (opcional)
      plantilla: tipoPlantilla,
      // 🆕 Guardamos la configuración técnica
      configTecnica: configTecnica,
    };
    try {
      const res = await api.post("/presupuestos", payload); 
      alert("Presupuesto creado");
  setDescripcion("");
      setCliente("");
      setItemsSeleccionados([]);
      load();
      // si backend devuelve PDF link directo:
      if (res && res.data && res.data.id) {
        // Llamar a la función de descarga segura y pasar el tipo de plantilla
        const created = { ...payload, id: res.data.id };
        handleDownloadPDF(created, tipoPlantilla);
      }
    } catch (err) {
      console.error("Error al crear presupuesto:", err);
      alert(`Error al crear presupuesto: ${err.response?.status === 403 ? 'Acceso Denegado (403)' : err.message}`);
    }
  };
  
  // 🆕 FUNCIÓN NUEVA: Manejar la Eliminación
  const handleDelete = async (id) => {
    if (window.confirm('¿Estás seguro de que quieres eliminar este presupuesto? Esta acción es irreversible.')) {
        try {
            // Usamos la función del servicio que usa api.delete con el token
            await deletePresupuesto(id);
            
            // Actualiza la lista en el frontend sin recargar todo
            setPresupuestos(presupuestos.filter(p => p.id !== id)); 
            
            alert('Presupuesto eliminado exitosamente.');
        } catch (error) {
            console.error("Error al eliminar presupuesto:", error);
            alert(`Error al eliminar presupuesto: ${error.response?.status === 403 ? 'Acceso Denegado (403)' : error.message}`);
        }
    }
  };

  // Generar PDF en cliente
  const generarPdfCliente = (presupuesto, plantilla = 'cercano', logo) => {
    const doc = new jsPDF();
    const fecha = new Date();
    const fechaStr = `${fecha.getDate()}/${fecha.getMonth() + 1}/${fecha.getFullYear()}`;
    const total = (presupuesto.items || []).reduce((s, it) => s + it.cantidad * it.precio, 0);

    // Obtener configuración técnica
    const rawConfig = presupuesto.configTecnica || configTecnica;
    
    // Normalizar configuración (sin valores por defecto - campos vacíos si usuario no completa)
    const config = {
      medidas: rawConfig.medidas || '',
      espesorPaneles: rawConfig.espesorPaneles || '',
      aislamiento: rawConfig.aislamiento || '',
      revestimiento: rawConfig.revestimiento || '',
      unionPaneles: rawConfig.unionPaneles || '',
      refuerzo: rawConfig.refuerzo || '',
      piso: rawConfig.piso || '',
      tipoPuerta: rawConfig.tipoPuerta || 'batiente',
      anchoPuerta: rawConfig.anchoPuerta || '',
      detallesPuerta: rawConfig.detallesPuerta || '',
      hpUnidadCercano: rawConfig.hpUnidadCercano || '',
      tipoMotorCercano: rawConfig.tipoMotorCercano || 'trifasica',
      marcaUnidadCercano: rawConfig.marcaUnidadCercano || '',
      hpEvaporadorCercano: rawConfig.hpEvaporadorCercano || '',
      tipoEvaporadorCercano: rawConfig.tipoEvaporadorCercano || '',
      hpUnidadInterior: rawConfig.hpUnidadInterior || '',
      tipoMotorInterior: rawConfig.tipoMotorInterior || 'monofasica',
      marcaUnidadInterior: rawConfig.marcaUnidadInterior || '',
      hpEvaporadorInterior: rawConfig.hpEvaporadorInterior || '',
      tipoEvaporadorInterior: rawConfig.tipoEvaporadorInterior || '',
      metrosCano: rawConfig.metrosCano || '',
      detallesMaterialesFrio: rawConfig.detallesMaterialesFrio || '',
      metrosCable: rawConfig.metrosCable || '',
      detallesMaterialesElectricos: rawConfig.detallesMaterialesElectricos || '',
      detallesTablero: rawConfig.detallesTablero || '',
      // AUTOMÁTICO según plantilla
      incluyeArmado: plantilla === 'cercano',  // cercano = incluye armado, interior = no incluye
      incluyeFlete: plantilla === 'interior'   // interior = flete a coordinar, cercano = no incluye
    };
    
    const margenIzq = 15;
    const margenDer = 195;
    const anchoUtil = margenDer - margenIzq;
    let y = dibujarEncabezadoPresupuesto(doc, logo, fechaStr, presupuesto.cliente);

    if (plantilla === 'cercano') {
      // ═══════════════════════════════════════════════════════════════
      // PLANTILLA CERCANO - Baja temperatura
      // ═══════════════════════════════════════════════════════════════
      
      doc.setTextColor(60, 60, 60);
      doc.text('Le enviamos a continuación el detalle del presupuesto solicitado.', margenIzq, y);
      doc.setTextColor(0, 0, 0);
      y += 12;

      // TÍTULO PRINCIPAL
      doc.setFontSize(14);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(0, 51, 102); // azul oscuro
      doc.text('Presupuesto – Cámara frigorífica de baja temperatura', margenIzq, y);
      doc.setTextColor(0, 0, 0);
      y += 10;

      // PRECIO DESTACADO
      doc.setFontSize(11);
      doc.setFont('helvetica', 'bold');
      doc.setFillColor(255, 243, 224);
      doc.rect(margenIzq - 2, y - 5, anchoUtil + 4, config.incluyeArmado ? 20 : 14, 'F');
      doc.text(`Precio Camara: $${total.toLocaleString('es-AR', {minimumFractionDigits: 2, maximumFractionDigits: 2})}`, margenIzq + 2, y + 2);
      y += 8;
      if (config.incluyeArmado) {
        doc.setFontSize(10);
        doc.setTextColor(0, 100, 0);
        doc.text('INCLUYE ARMADO COMPLETO', margenIzq + 2, y + 2);
        y += 6;
      }
      if (!config.incluyeFlete) {
        doc.setTextColor(220, 50, 50);
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(9);
        doc.text('(no incluye flete)', margenIzq + 2, y + 2);
        y += 6;
      }
      doc.setTextColor(0, 0, 0);
      y += 6;

      // ESPECIFICACIONES TECNICAS
      doc.setFontSize(11);
      doc.setFont('helvetica', 'bold');
      doc.text('ESPECIFICACIONES TECNICAS', margenIzq, y);
      y += 7;
      
      doc.setFontSize(9);
      doc.setFont('helvetica', 'normal');
      
      const specs = [];
      if (config.medidas) specs.push({ label: 'Medidas:', value: `${config.medidas} metros (frente x profundidad x altura)` });
      if (config.espesorPaneles) specs.push({ label: 'Paneles:', value: `Espesor ${config.espesorPaneles}` });
      if (config.aislamiento) specs.push({ label: 'Aislamiento:', value: config.aislamiento });
      if (config.revestimiento) specs.push({ label: 'Revestimiento:', value: config.revestimiento });
      if (config.unionPaneles) specs.push({ label: 'Union paneles:', value: config.unionPaneles });
      if (config.refuerzo) specs.push({ label: 'Refuerzo:', value: config.refuerzo });
      if (config.piso) specs.push({ label: 'Piso:', value: config.piso });
      if (config.anchoPuerta || config.detallesPuerta) {
        const puertaVal = `${config.tipoPuerta.charAt(0).toUpperCase() + config.tipoPuerta.slice(1)}${config.anchoPuerta ? ' ' + config.anchoPuerta : ''}${config.detallesPuerta ? ' ' + config.detallesPuerta : ''}`;
        specs.push({ label: 'Puerta:', value: puertaVal });
      }
      
      specs.forEach(spec => {
        doc.setFont('helvetica', 'bold');
        doc.text(`- ${spec.label}`, margenIzq + 2, y);
        doc.setFont('helvetica', 'normal');
        const lineas = doc.splitTextToSize(spec.value, anchoUtil - 30);
        doc.text(lineas, margenIzq + 30, y);
        y += lineas.length * 5;
      });
      if (specs.length > 0) y += 3;

      // EQUIPAMIENTO
      doc.setFontSize(11);
      doc.setFont('helvetica', 'bold');
      doc.text('EQUIPAMIENTO INCLUIDO', margenIzq, y);
      y += 7;
      
      doc.setFontSize(9);
      doc.setFont('helvetica', 'normal');
      
      const equip = [];
      if (config.hpUnidadCercano || config.marcaUnidadCercano) {
        equip.push(`Unidad condensadora ${config.hpUnidadCercano || ''} HP ${config.tipoMotorCercano}${config.marcaUnidadCercano ? ' marca ' + config.marcaUnidadCercano : ''}`);
      }
      if (config.hpEvaporadorCercano || config.tipoEvaporadorCercano) {
        equip.push(`Evaporador ${config.tipoEvaporadorCercano || ''} ${config.hpEvaporadorCercano || ''} HP`);
      }
      
      equip.forEach(item => {
        doc.text(`- ${item}`, margenIzq + 2, y);
        y += 5;
      });
      if (equip.length > 0) y += 3;

      // MATERIALES
      if (config.metrosCano || config.detallesMaterialesFrio) {
        doc.setFontSize(10);
        doc.setFont('helvetica', 'bold');
        doc.text('Materiales de frio:', margenIzq + 2, y);
        y += 5;
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(9);
        doc.text(`- ${config.metrosCano || ''} ${config.detallesMaterialesFrio || ''}`.trim(), margenIzq + 5, y);
        y += 7;
      }
      
      if (config.metrosCable || config.detallesMaterialesElectricos) {
        doc.setFontSize(10);
        doc.setFont('helvetica', 'bold');
        doc.text('Materiales electricos:', margenIzq + 2, y);
        y += 5;
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(9);
        doc.text(`- ${config.metrosCable || ''} ${config.detallesMaterialesElectricos || ''}`.trim(), margenIzq + 5, y);
        y += 7;
      }
      
      if (config.detallesTablero) {
        doc.setFontSize(10);
        doc.setFont('helvetica', 'bold');
        doc.text('Tablero electrico completo:', margenIzq + 2, y);
        y += 5;
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(9);
        const tableroLineas = doc.splitTextToSize(`- ${config.detallesTablero}`, anchoUtil - 10);
        tableroLineas.forEach(linea => {
          doc.text(linea, margenIzq + 5, y);
          y += 4;
        });
        y += 6;
      }

      // FOOTER
      doc.setDrawColor(200, 200, 200);
      doc.line(margenIzq, y, margenDer, y);
      y += 6;
      doc.setFontSize(9);
      doc.setFont('helvetica', 'bold');
      doc.text('Flete:', margenIzq, y);
      doc.setFont('helvetica', 'normal');
      doc.text(config.incluyeFlete ? 'Incluido' : 'No incluido (a coordinar)', margenIzq + 20, y);
      y += 6;
      doc.setFont('helvetica', 'bold');
      doc.text('Validez:', margenIzq, y);
      doc.setFont('helvetica', 'normal');
      doc.text('24 horas', margenIzq + 20, y);
      y += 6;
      dibujarContactoPresupuesto(doc, y, margenIzq, margenDer);

    } else {
      // ═══════════════════════════════════════════════════════════════
      // PLANTILLA INTERIOR - Media temperatura
      // ═══════════════════════════════════════════════════════════════
      
      // TÍTULO PRINCIPAL
      doc.setFontSize(14);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(0, 102, 51); // verde oscuro
      doc.text('Presupuesto – Cámara frigorífica de media temperatura', margenIzq, y);
      doc.setTextColor(0, 0, 0);
      y += 10;

      // PRECIO Y FACTURACION
      doc.setFontSize(11);
      doc.setFont('helvetica', 'bold');
      doc.setFillColor(240, 255, 240);
      doc.rect(margenIzq - 2, y - 5, anchoUtil + 4, 22, 'F');
      doc.text(`Precio Camara: $${total.toLocaleString('es-AR', {minimumFractionDigits: 2, maximumFractionDigits: 2})}`, margenIzq + 2, y + 2);
      y += 8;
      doc.setFontSize(10);
      doc.setTextColor(0, 100, 0);
      doc.text('PRECIO FINAL CON IVA - Factura A o B', margenIzq + 2, y + 2);
      doc.setTextColor(0, 0, 0);
      y += 10;
      
      doc.setFontSize(9);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(204, 102, 0);
      doc.text('PANELES DE PRIMERA CALIDAD', margenIzq + 2, y + 2);
      doc.setTextColor(0, 0, 0);
      y += 16;

      // ESPECIFICACIONES
      doc.setFontSize(11);
      doc.setFont('helvetica', 'bold');
      doc.text('ESPECIFICACIONES', margenIzq, y);
      y += 7;
      
      doc.setFontSize(9);
      doc.setFont('helvetica', 'normal');
      
      const specsInt = [];
      if (config.medidas) specsInt.push({ label: 'Medidas:', value: `${config.medidas} metros (frente x profundidad x altura)` });
      if (config.espesorPaneles) specsInt.push({ label: 'Paneles:', value: `Espesor ${config.espesorPaneles}` });
      if (config.aislamiento) specsInt.push({ label: 'Aislamiento:', value: config.aislamiento });
      if (config.revestimiento) specsInt.push({ label: 'Revestimiento:', value: config.revestimiento });
      if (config.unionPaneles) specsInt.push({ label: 'Union paneles:', value: config.unionPaneles });
      
      specsInt.forEach(spec => {
        doc.setFont('helvetica', 'bold');
        doc.text(`- ${spec.label}`, margenIzq + 2, y);
        doc.setFont('helvetica', 'normal');
        const lineas = doc.splitTextToSize(spec.value, anchoUtil - 30);
        doc.text(lineas, margenIzq + 30, y);
        y += lineas.length * 5;
      });
      if (specsInt.length > 0) y += 4;

      // MATERIALES INCLUIDOS
      const incluyeItems = [];
      if (config.refuerzo) incluyeItems.push(config.refuerzo);
      if (config.piso) incluyeItems.push(config.piso);
      if (config.anchoPuerta || config.detallesPuerta) {
        incluyeItems.push(`Puerta ${config.tipoPuerta}${config.anchoPuerta ? ' ' + config.anchoPuerta : ''}${config.detallesPuerta ? ' ' + config.detallesPuerta : ''}`);
      }
      
      if (incluyeItems.length > 0) {
        doc.setFontSize(11);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(0, 100, 0);
        doc.text('INCLUYE:', margenIzq, y);
        doc.setTextColor(0, 0, 0);
        y += 7;
        
        doc.setFontSize(9);
        doc.setFont('helvetica', 'normal');
        
        incluyeItems.forEach(item => {
          const lineas = doc.splitTextToSize(`- ${item}`, anchoUtil - 5);
          lineas.forEach(linea => {
            doc.text(linea, margenIzq + 2, y);
            y += 5;
          });
        });
        y += 4;
      }

      // EQUIPAMIENTO
      const equipInt = [];
      if (config.hpUnidadInterior || config.marcaUnidadInterior) {
        equipInt.push(`${config.hpUnidadInterior || ''} HP Unidad condensadora ${config.tipoMotorInterior}${config.marcaUnidadInterior ? ' marca ' + config.marcaUnidadInterior : ''}`);
      }
      if (config.hpEvaporadorInterior || config.tipoEvaporadorInterior) {
        equipInt.push(`Evaporador ${config.tipoEvaporadorInterior || ''} ${config.hpEvaporadorInterior || ''} HP`);
      }
      if (config.detallesTablero) equipInt.push(`Tablero: ${config.detallesTablero}`);
      if (config.metrosCano || config.detallesMaterialesFrio) {
        equipInt.push(`Materiales frio: ${config.metrosCano || ''} ${config.detallesMaterialesFrio || ''}`.trim());
      }
      
      if (equipInt.length > 0) {
        doc.setFontSize(11);
        doc.setFont('helvetica', 'bold');
        doc.text('EQUIPAMIENTO', margenIzq, y);
        y += 7;
        
        doc.setFontSize(9);
        doc.setFont('helvetica', 'normal');
        
        equipInt.forEach(item => {
          const lineas = doc.splitTextToSize(`- ${item}`, anchoUtil - 5);
          lineas.forEach(linea => {
            doc.text(linea, margenIzq + 2, y);
            y += 5;
          });
        });
        y += 5;
      }

      // IMPORTANTE
      if (!config.incluyeArmado) {
        doc.setFillColor(255, 240, 240);
        doc.rect(margenIzq - 2, y - 3, anchoUtil + 4, 10, 'F');
        doc.setFontSize(10);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(180, 0, 0);
        doc.text('NO INCLUYE ARMADO', margenIzq + 2, y + 3);
        doc.setTextColor(0, 0, 0);
        y += 14;
      } else {
        doc.setFillColor(240, 255, 240);
        doc.rect(margenIzq - 2, y - 3, anchoUtil + 4, 10, 'F');
        doc.setFontSize(10);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(0, 100, 0);
        doc.text('INCLUYE ARMADO COMPLETO', margenIzq + 2, y + 3);
        doc.setTextColor(0, 0, 0);
        y += 14;
      }

      // FOOTER
      doc.setDrawColor(200, 200, 200);
      doc.line(margenIzq, y, margenDer, y);
      y += 6;
      
      doc.setFontSize(9);
      doc.setFont('helvetica', 'bold');
      doc.text('Flete:', margenIzq, y);
      doc.setFont('helvetica', 'normal');
      doc.text(config.incluyeFlete ? 'Incluido' : 'A coordinar', margenIzq + 20, y);
      y += 6;
      doc.setFont('helvetica', 'bold');
      doc.text('Validez:', margenIzq, y);
      doc.setFont('helvetica', 'normal');
      doc.text('24 horas', margenIzq + 20, y);
      y += 10;

      dibujarContactoPresupuesto(doc, y, margenIzq, margenDer);
    }

    doc.save(`presupuesto_${presupuesto.id || 'local'}_${plantilla}.pdf`);
  };

  // DESCARGA PDF - SIEMPRE GENERA EN CLIENTE con la configuración personalizada
  const handleDownloadPDF = async (p, plantilla = 'cercano') => {
    // 🔥 GENERAR SIEMPRE EN CLIENTE para usar configTecnica personalizada
    try {
      const logo = await cargarLogoShastino();
      generarPdfCliente(p, plantilla, logo);
    } catch (error) {
      console.error("Error al generar el PDF del presupuesto:", error);
      alert("No se pudo generar el PDF. Verificá que el logo esté disponible e intentá nuevamente.");
    }
    
    /* CÓDIGO ANTERIOR (backend no usa configTecnica):
    try {
      const res = await api.get(`/presupuestos/${p.id}/pdf`, {
        responseType: 'blob',
      });
      const pdfBlob = res.data;
      const url = window.URL.createObjectURL(pdfBlob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `presupuesto_${p.id}_${p.cliente}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.warn("Error backend, generando en cliente:", err);
      generarPdfCliente(p, plantilla);
    }
    */
  };

  return (
    <div className="p-8">
      <h2 className="text-2xl mb-4">Presupuestos</h2>

      {/* FORMULARIO BÁSICO */}
      <div className="flex gap-2 mb-4">
        <input type="text" placeholder="Cliente" className="border p-2" value={cliente} onChange={(e) => setCliente(e.target.value)} />
        <input type="text" placeholder="Descripción" className="border p-2" value={descripcion} onChange={(e) => setDescripcion(e.target.value)} />
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            <input type="radio" name="plantilla" checked={tipoPlantilla === 'cercano'} onChange={() => setTipoPlantilla('cercano')} /> Cercano
          </label>
          <label style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            <input type="radio" name="plantilla" checked={tipoPlantilla === 'interior'} onChange={() => setTipoPlantilla('interior')} /> Interior
          </label>
        </div>
      </div>

      {/* 🆕 CONFIGURACIÓN TÉCNICA EXPANDIBLE */}
      <details className="mb-4 border border-gray-300 rounded p-4 bg-gray-50">
        <summary className="cursor-pointer font-semibold text-lg mb-3">⚙️ Configuración Técnica (opcional)</summary>
        
        <div className="grid grid-cols-3 gap-3 mt-3">
          {/* COLUMNA 1: Medidas y Especificaciones de Paneles */}
          <div className="space-y-2">
            <h4 className="font-semibold text-sm mb-2 text-blue-700">Medidas y Paneles</h4>
            <label className="block text-xs">
              Medidas (frente x prof x alto):
              <input type="text" className="border p-1 w-full text-sm" placeholder="4.00 x 4.00 x 2.06" value={configTecnica.medidas} onChange={(e) => setConfigTecnica({...configTecnica, medidas: e.target.value})} />
            </label>
            <label className="block text-xs">
              Espesor paneles:
              <input type="text" className="border p-1 w-full text-sm" placeholder="60 mm" value={configTecnica.espesorPaneles} onChange={(e) => setConfigTecnica({...configTecnica, espesorPaneles: e.target.value})} />
            </label>
            <label className="block text-xs">
              Aislamiento:
              <textarea className="border p-1 w-full text-xs" rows="2" placeholder="Espuma de poliuretano..." value={configTecnica.aislamiento} onChange={(e) => setConfigTecnica({...configTecnica, aislamiento: e.target.value})} />
            </label>
            <label className="block text-xs">
              Revestimiento:
              <textarea className="border p-1 w-full text-xs" rows="2" placeholder="Chapa blanca prepintada..." value={configTecnica.revestimiento} onChange={(e) => setConfigTecnica({...configTecnica, revestimiento: e.target.value})} />
            </label>
            <label className="block text-xs">
              Union paneles:
              <textarea className="border p-1 w-full text-xs" rows="2" placeholder="Sistema encastre macho/hembra..." value={configTecnica.unionPaneles} onChange={(e) => setConfigTecnica({...configTecnica, unionPaneles: e.target.value})} />
            </label>
            <label className="block text-xs">
              Refuerzo:
              <textarea className="border p-1 w-full text-xs" rows="2" placeholder="Perfil 40x40mm..." value={configTecnica.refuerzo} onChange={(e) => setConfigTecnica({...configTecnica, refuerzo: e.target.value})} />
            </label>
            <label className="block text-xs">
              Piso:
              <input type="text" className="border p-1 w-full text-sm" placeholder="NO INCLUIDO o Zocalo sanitario" value={configTecnica.piso} onChange={(e) => setConfigTecnica({...configTecnica, piso: e.target.value})} />
            </label>
            
            <h5 className="font-semibold text-xs mt-3 text-blue-600">Puerta</h5>
            <label className="block text-xs">
              Tipo:
              <select className="border p-1 w-full text-sm" value={configTecnica.tipoPuerta} onChange={(e) => setConfigTecnica({...configTecnica, tipoPuerta: e.target.value})}>
                <option value="batiente">Batiente</option>
                <option value="corrediza">Corrediza</option>
              </select>
            </label>
            <label className="block text-xs">
              Ancho:
              <input type="text" className="border p-1 w-full text-sm" placeholder="0.90m" value={configTecnica.anchoPuerta} onChange={(e) => setConfigTecnica({...configTecnica, anchoPuerta: e.target.value})} />
            </label>
            <label className="block text-xs">
              Detalles puerta:
              <input type="text" className="border p-1 w-full text-sm" placeholder="con herrajes y marco calefaccionado" value={configTecnica.detallesPuerta} onChange={(e) => setConfigTecnica({...configTecnica, detallesPuerta: e.target.value})} />
            </label>
          </div>

          {/* COLUMNA 2: Equipamiento según plantilla */}
          <div className="space-y-2">
            <h4 className="font-semibold text-sm mb-2 text-blue-700">Equipamiento ({tipoPlantilla === 'cercano' ? 'Cercano' : 'Interior'})</h4>
            {tipoPlantilla === 'cercano' ? (
              <>
                <label className="block text-xs">
                  Marca unidad:
                  <input type="text" className="border p-1 w-full text-sm" placeholder="Good Cold" value={configTecnica.marcaUnidadCercano} onChange={(e) => setConfigTecnica({...configTecnica, marcaUnidadCercano: e.target.value})} />
                </label>
                <label className="block text-xs">
                  HP Unidad:
                  <input type="text" className="border p-1 w-full text-sm" placeholder="3" value={configTecnica.hpUnidadCercano} onChange={(e) => setConfigTecnica({...configTecnica, hpUnidadCercano: e.target.value})} />
                </label>
                <label className="block text-xs">
                  Tipo motor:
                  <select className="border p-1 w-full text-sm" value={configTecnica.tipoMotorCercano} onChange={(e) => setConfigTecnica({...configTecnica, tipoMotorCercano: e.target.value})}>
                    <option value="monofásica">Monofásica</option>
                    <option value="trifásica">Trifásica</option>
                  </select>
                </label>
                <label className="block text-xs">
                  HP Evaporador:
                  <input type="text" className="border p-1 w-full text-sm" placeholder="3" value={configTecnica.hpEvaporadorCercano} onChange={(e) => setConfigTecnica({...configTecnica, hpEvaporadorCercano: e.target.value})} />
                </label>
                <label className="block text-xs">
                  Tipo evaporador:
                  <input type="text" className="border p-1 w-full text-sm" placeholder="cubico" value={configTecnica.tipoEvaporadorCercano} onChange={(e) => setConfigTecnica({...configTecnica, tipoEvaporadorCercano: e.target.value})} />
                </label>
              </>
            ) : (
              <>
                <label className="block text-xs">
                  Marca unidad:
                  <input type="text" className="border p-1 w-full text-sm" placeholder="Good Cold" value={configTecnica.marcaUnidadInterior} onChange={(e) => setConfigTecnica({...configTecnica, marcaUnidadInterior: e.target.value})} />
                </label>
                <label className="block text-xs">
                  HP Unidad:
                  <input type="text" className="border p-1 w-full text-sm" placeholder="1" value={configTecnica.hpUnidadInterior} onChange={(e) => setConfigTecnica({...configTecnica, hpUnidadInterior: e.target.value})} />
                </label>
                <label className="block text-xs">
                  Tipo motor:
                  <select className="border p-1 w-full text-sm" value={configTecnica.tipoMotorInterior} onChange={(e) => setConfigTecnica({...configTecnica, tipoMotorInterior: e.target.value})}>
                    <option value="monofásica">Monofásica</option>
                    <option value="trifásica">Trifásica</option>
                  </select>
                </label>
                <label className="block text-xs">
                  HP Evaporador:
                  <input type="text" className="border p-1 w-full text-sm" placeholder="1" value={configTecnica.hpEvaporadorInterior} onChange={(e) => setConfigTecnica({...configTecnica, hpEvaporadorInterior: e.target.value})} />
                </label>
                <label className="block text-xs">
                  Tipo evaporador:
                  <input type="text" className="border p-1 w-full text-sm" placeholder="lateral forzado" value={configTecnica.tipoEvaporadorInterior} onChange={(e) => setConfigTecnica({...configTecnica, tipoEvaporadorInterior: e.target.value})} />
                </label>
              </>
            )}
          </div>

          {/* COLUMNA 3: Materiales y Opciones */}
          <div className="space-y-2">
            <h4 className="font-semibold text-sm mb-2 text-blue-700">Materiales y Opciones</h4>
            
            <h5 className="font-semibold text-xs mt-2 text-blue-600">Materiales de Frio</h5>
            <label className="block text-xs">
              Metros caño:
              <input type="text" className="border p-1 w-full text-sm" placeholder="4 metros" value={configTecnica.metrosCano} onChange={(e) => setConfigTecnica({...configTecnica, metrosCano: e.target.value})} />
            </label>
            <label className="block text-xs">
              Detalles materiales frio:
              <textarea className="border p-1 w-full text-xs" rows="2" placeholder="metros + aislantes, filtro, valvula" value={configTecnica.detallesMaterialesFrio} onChange={(e) => setConfigTecnica({...configTecnica, detallesMaterialesFrio: e.target.value})} />
            </label>
            
            <h5 className="font-semibold text-xs mt-2 text-blue-600">Materiales Electricos</h5>
            <label className="block text-xs">
              Metros cable:
              <input type="text" className="border p-1 w-full text-sm" placeholder="8 metros" value={configTecnica.metrosCable} onChange={(e) => setConfigTecnica({...configTecnica, metrosCable: e.target.value})} />
            </label>
            <label className="block text-xs">
              Detalles materiales electricos:
              <textarea className="border p-1 w-full text-xs" rows="2" placeholder="metros + contactor + relevo termico" value={configTecnica.detallesMaterialesElectricos} onChange={(e) => setConfigTecnica({...configTecnica, detallesMaterialesElectricos: e.target.value})} />
            </label>
            
            <h5 className="font-semibold text-xs mt-2 text-blue-600">Tablero Electrico</h5>
            <label className="block text-xs">
              Detalles tablero:
              <textarea className="border p-1 w-full text-xs" rows="3" placeholder="Llave corte general + encendido motor + ..." value={configTecnica.detallesTablero} onChange={(e) => setConfigTecnica({...configTecnica, detallesTablero: e.target.value})} />
            </label>
            
            <div className="mt-3 pt-3 border-t text-xs text-gray-500">
              <p className="font-semibold mb-2 text-blue-600">Datos Fijos:</p>
              <p>- Validez: 24 horas</p>
              <p>- Fecha: Automatica</p>
              <p>- Contacto: Datos de empresa</p>
              <p className="mt-2 font-semibold text-blue-600">Automatico segun plantilla:</p>
              <p>- <span className="font-semibold">Cercano:</span> Armado INCLUIDO, Flete NO incluido</p>
              <p>- <span className="font-semibold">Interior:</span> Armado NO incluido, Flete A coordinar</p>
            </div>
          </div>
        </div>
      </details>

      {/* BOTÓN CREAR */}
      <div className="mb-4">
        <button onClick={handleCreate} className="bg-green-500 text-white px-6 py-2 rounded font-semibold hover:bg-green-600">
          ✓ Crear Presupuesto
        </button>
      </div>

      {/* PRODUCTOS DISPONIBLES */}
      <div>
        <h3>Productos disponibles (clic para agregar al presupuesto)</h3>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          {productos.map((pr) => (
            <div key={pr.id} style={{ border: "1px solid #ccc", padding: 8, borderRadius: 6 }}>
              <div style={{ fontWeight: "bold" }}>{pr.nombre}</div>
              <div style={{ fontSize: 12 }}>
                Precio aprox: ${formatNumber((pr.costoTotal ?? pr.items.reduce((s, it) => s + Number(it.costo || 0), 0)) + (pr.gananciaGenerada ?? 0), 2)}
              </div>
              <button className="button" onClick={() => handleAddItem(pr)}>Agregar</button>
            </div>
          ))}
        </div>
      </div>

      {/* ITEMS DEL PRESUPUESTO */}
      <div style={{ marginTop: 12 }}>
        <h3>Items del presupuesto</h3>
        <table className="table">
          <thead>
            <tr><th>Concepto</th><th>Cantidad</th><th>Precio</th><th>Subtotal</th><th></th></tr>
          </thead>
          <tbody>
            {itemsSeleccionados.map((it, idx) => (
              <tr key={idx}>
                <td>{it.nombre}</td>
                <td>{it.cantidad}</td>
                <td>${formatNumber(it.precio, 2)}</td>
                <td>${formatNumber(it.cantidad * it.precio, 2)}</td>
                <td><button className="button" onClick={() => handleRemoveItem(it.nombre)}>Quitar</button></td>
              </tr>
            ))}
          </tbody>
        </table>

        <div style={{ marginTop: 8 }}>
          <strong>Total: </strong>
          ${formatNumber(itemsSeleccionados.reduce((s, it) => s + it.cantidad * it.precio, 0), 2)}
        </div>
      </div>

      <hr style={{ margin: "16px 0" }} />

      <h3 className="text-xl font-bold mb-3">Presupuestos guardados</h3>
      <ul className="space-y-2">
        {presupuestos.map((p) => (
          <li key={p.id} className="flex justify-between items-center border border-gray-300 rounded-lg p-4 bg-white shadow-sm hover:shadow-md transition-shadow">
            <div>
              <div className="font-semibold text-lg">{p.cliente}</div>
              <div className="text-sm text-gray-600">{p.descripcion}</div>
            </div>
            <div className="flex gap-2">
              <button 
                onClick={() => handleDownloadPDF(p, 'cercano')} 
                className="bg-orange-500 hover:bg-orange-600 text-white px-4 py-2 rounded font-semibold transition-colors"
                title="Descargar PDF - Plantilla Cercano (Baja Temperatura)"
              >
                📄 Cercano
              </button>
              <button 
                onClick={() => handleDownloadPDF(p, 'interior')} 
                className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded font-semibold transition-colors"
                title="Descargar PDF - Plantilla Interior (Media Temperatura)"
              >
                📄 Interior
              </button>
              <button 
                onClick={() => handleDelete(p.id)} 
                className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded font-semibold transition-colors" 
                title="Eliminar presupuesto permanentemente"
              >
                🗑️ Eliminar
              </button>
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}